import { Injectable, inject } from '@angular/core';
import { ListResponse } from '@models/helpers';
import { DataListType } from '@models/module/fields/main';
import { FilterModel, GridSearchEntityInstance, SearchViewModel } from '@models/module/grid';
import { GridFieldsModel } from '@models/module/main';
import { HeaderFooterData } from '@module-instance/dynamic-module/common';
import { HYBRID_MODULE_ROUTE_PARTS } from './hybrid-api.model';

import { DataListField } from '@shared/components/data-list/common';
import { HttpService } from '@shared/services';

export type ModuleGriRequestParams<B = unknown> = {
  body?: B;
  contractId?: number;
  moduleId?: number;
  relationId?: number;
  workflowId?: number;
  parentId?: number;
  parentEntityInstanceId?: number;
  dataListType?: DataListType;
  /** Used id for nested relations to filter elements */
  fieldId?: number;
  /** used at least for GlobalFilterId */
  id?: number;
};

@Injectable({
  providedIn: 'root',
})
export class ModuleGridService {
  private http = inject(HttpService);

  private getHybridURL(moduleId: number, urlPart: string): string {
    const controller = HYBRID_MODULE_ROUTE_PARTS[moduleId] || 'ModuleGrid';
    return `_api/${controller}/${urlPart}`;
  }

  getGridFields({ contractId, moduleId, relationId, workflowId, dataListType }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ contractId, workflowId, relationId, dataListType });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.get<GridFieldsModel>(this.getHybridURL(moduleId, 'GridFields'), {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  gridSearch({ body, contractId, moduleId, dataListType }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ contractId, dataListType });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<ListResponse<GridSearchEntityInstance>>(this.getHybridURL(moduleId, 'GridSearch'), body, {
      useLoader: false,
      httpOptions: { params, headers },
    });
  }

  gridSearchChildren({ body, contractId, moduleId, relationId, parentEntityInstanceId, fieldId }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ contractId, relationId, parentEntityInstanceId, fieldId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<ListResponse<GridSearchEntityInstance>>('_api/ModuleGrid/GridSearchChildren', body, {
      useLoader: false,
      httpOptions: { params, headers },
    });
  }

  gridSidebarSearch({ body, contractId, moduleId }: ModuleGriRequestParams<SearchViewModel>) {
    const params = this.http.buildHttpParams({ contractId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    
    // skip sorting and filtering
    delete body.sort;
    delete body.filter;

    return this.http.post<ListResponse<GridSearchEntityInstance> & { descriptionField: DataListField }>(
      '_api/ModuleGrid/GridSidebarSearch',
      body,
      {
        useLoader: false,
        httpOptions: { params, headers },
      },
    );
  }

  gridExpressions({ contractId, moduleId, body }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ contractId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<HeaderFooterData>('_api/ModuleGrid/GridExpressions', body, {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  gridExpresionsChildren({ contractId, moduleId, body, relationId, parentEntityInstanceId, fieldId }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ contractId, relationId, parentEntityInstanceId, fieldId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.post<HeaderFooterData>('_api/ModuleGrid/GridExpressionsChildren', body, {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }

  getGlobalFilter({ id, contractId, moduleId }: ModuleGriRequestParams) {
    const params = this.http.buildHttpParams({ id, contractId });
    const headers = moduleId ? { moduleId: moduleId.toString() } : undefined;
    return this.http.get<FilterModel>('_api/ModuleGrid/GetFilter', {
      httpOptions: { params, headers },
      useLoader: false,
    });
  }
}
